import { createSelector } from "@reduxjs/toolkit";

import type { RootState } from "Store/configureStore";

interface SelectorProps {
  organizationId: string;
}

const selectCarbonEmissions = (state: RootState) => state.carbonEmissions;
const selectOrgId = (_: RootState, { organizationId }: SelectorProps) =>
  organizationId;

// Basic state selectors
export const selectCarbonEmissionsStateByOrganizationId = createSelector(
  selectCarbonEmissions,
  selectOrgId,
  (state, organizationId) =>
    state.byOrganizationId[organizationId] || {
      data: null,
      isLoading: false,
      error: null
    }
);

export const selectCarbonEmissionsDataByOrganizationId = createSelector(
  selectCarbonEmissionsStateByOrganizationId,
  state => state.data
);

export const selectCarbonEmissionsIsLoadingByOrganizationId = createSelector(
  selectCarbonEmissionsStateByOrganizationId,
  state => state.isLoading
);

export const selectCarbonEmissionsErrorByOrganizationId = createSelector(
  selectCarbonEmissionsStateByOrganizationId,
  state => state.error
);

// Raw data selector
export const selectCarbonEmissionsRawData = createSelector(
  selectCarbonEmissionsDataByOrganizationId,
  data => data?.rawData || { projects: [] }
);

// Projects selector
export const selectCarbonEmissionsProjects = createSelector(
  selectCarbonEmissionsRawData,
  rawData => rawData.projects || []
);

// Extract monthly data with minimal transformations
export const selectCarbonEmissionsMonthlyData = createSelector(
  selectCarbonEmissionsProjects,
  projects => {
    // Extract all monthly values from all projects
    const monthlyMap = new Map<string, number>();

    projects.forEach(project => {
      if (project.values && project.values.length > 0) {
        project.values.forEach(value => {
          const month = value.start_time.substring(0, 7); // Get YYYY-MM format
          const currentValue = monthlyMap.get(month) || 0;
          monthlyMap.set(month, currentValue + value.value);
        });
      }
    });

    // Convert map to array and sort by month
    return Array.from(monthlyMap.entries())
      .map(([month, value]) => ({ month, value }))
      .sort((a, b) => a.month.localeCompare(b.month));
  }
);

// Calculate total CO2 for previous year
export const selectCarbonEmissionsPreviousYearTotal = createSelector(
  selectCarbonEmissionsMonthlyData,
  monthlyData => {
    if (!monthlyData || monthlyData.length === 0) {
      return 0;
    }

    return monthlyData.reduce((total, month) => total + month.value, 0);
  }
);

// Format monthly data for chart display
export const selectCarbonEmissionsMonthlyDataForChart = createSelector(
  [selectCarbonEmissionsMonthlyData],
  (monthlyData): { label: string; value: number }[] => {
    // If there's no data, return empty array
    if (!monthlyData || monthlyData.length === 0) {
      return [];
    }

    // Transform data to the expected chart format
    return monthlyData.map(item => {
      // Convert YYYY-MM format to MMM format (e.g., "2023-01" to "Jan")
      let label: string;
      try {
        const [, month] = item.month.split("-");
        const date = new Date(2000, parseInt(month) - 1, 1); // Using arbitrary year for month formatting

        // Format as "MMM" (Jan, Feb, etc.)
        label = date.toLocaleString("en-US", { month: "short" }).toUpperCase();
      } catch {
        // Fallback if parsing fails
        label = item.month;
      }

      return {
        label,
        value: item.value
      };
    });
  }
);

// Calculate per-project totals for the projects table
export const selectCarbonEmissionsProjectsTableData = createSelector(
  selectCarbonEmissionsProjects,
  projects => {
    return projects
      .map(project => {
        // Check if the project has any values
        const hasValues = project.values && project.values.length > 0;

        // Calculate total emissions for this project if it has values
        const totalEmissions = hasValues
          ? project.values.reduce((sum, value) => sum + value.value, 0)
          : null; // Use null to indicate "No data"

        return {
          projectId: project.project_id,
          projectName: project.project_title || `Project ${project.project_id}`,
          totalEmissions
        };
      })
      .sort((a, b) => {
        // Sort projects with data first, then by emission amount
        if (a.totalEmissions === null && b.totalEmissions !== null) return 1;
        if (a.totalEmissions !== null && b.totalEmissions === null) return -1;
        if (a.totalEmissions === null && b.totalEmissions === null) return 0;
        return (b.totalEmissions || 0) - (a.totalEmissions || 0);
      });
  }
);
